import React, { useState, useCallback, createContext } from 'react';
import { GridRowParams } from '@material-ui/data-grid';
import { toast } from 'react-toastify';
import api from '~/services/api';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { schema, schemaModal } from '../Validations';
import {
  subModuloProps,
  ModuloProviderProps,
  ModuloContextDataProps,
} from '../types';
import { addUpdate, MasterDetailProps } from '~/utils/masterDetail';
import { nanoid } from 'nanoid';

export const ModuloContext = createContext({} as ModuloContextDataProps);

export const ModuloContextProvider: React.FC<ModuloProviderProps> = ({
  children,
}) => {
  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    reset,
    watch,
    formState,
    setFocus,
    clearErrors,
    setError,
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onSubmit',
  });
  const formGeral = {
    register,
    getValues,
    handleSubmit,
    formState,
    control,
    setValue,
    watch,
    reset,
    setFocus,
    clearErrors,
    setError,
  };

  const {
    register: registerModal,
    handleSubmit: handleSubmitModal,
    control: controlModal,
    getValues: getValuesModal,
    setValue: setValueModal,
    reset: resetModal,
    watch: watchModal,
    formState: formStateModal,
    setFocus: setFocusModal,
    clearErrors: clearErrorsModal,
    setError: setErrorModal,
  } = useForm({
    resolver: yupResolver(schemaModal),
    reValidateMode: 'onSubmit',
  });
  const formModal = {
    register: registerModal,
    getValues: getValuesModal,
    handleSubmit: handleSubmitModal,
    formState: formStateModal,
    control: controlModal,
    setValue: setValueModal,
    watch: watchModal,
    reset: resetModal,
    setFocus: setFocusModal,
    clearErrors: clearErrorsModal,
    setError: setErrorModal,
  };
  const [flgSuperAdm, setFlgSuperAdm] = useState<boolean>(false);
  const [flgInativo, setFlgInativo] = useState<boolean>(false);
  const [isUpdate, setUpdate] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(true);
  const [loadingTable, setLoadingTable] = useState<boolean>(false);
  const [rows, setRows] = useState<subModuloProps[]>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<subModuloProps | null>(null);
  const [openOrderModal, setOpenOrderModal] = useState<boolean>(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const masterDetailDefault = [
    {
      obj_name: 'subModulos',
      pk_fields: ['cod_seq_modulo_secao'],
      itens: {
        insert: [],
        update: [],
        delete: [],
      },
    },
  ];
  const [masterDetail, setMasterDetail] =
    useState<MasterDetailProps[]>(masterDetailDefault);

  const resetFormData = useCallback(() => {
    reset({
      des_modulo: '',
      cod_modulo: null,
    });
    setFlgSuperAdm(false);
    setFlgInativo(false);
    setUpdate(false);
    setIsEditing(null);
    setRows([]);
    setMasterDetail(masterDetailDefault);
  }, [masterDetailDefault, reset]);

  const onRowClick = useCallback(
    async (param: GridRowParams) => {
      setUpdate(true);
      setShowSearch(false);
      const { row } = param;

      const { des_modulo, flg_inativo, flg_requer_superadmin, cod_modulo } =
        row;

      reset({
        des_modulo,
        cod_modulo,
      });
      setFlgSuperAdm(flg_requer_superadmin);
      setFlgInativo(flg_inativo);

      const { data } = await api.get(`/modulo-sub-modulo/${cod_modulo}`);
      if (data.success) {
        setLoadingTable(true);
        const subModulos = data.data.map((subModulo: subModuloProps) => {
          const uuid = nanoid();
          return {
            uuid,
            cod_seq_modulo_secao: subModulo.cod_seq_modulo_secao,
            num_ordem_menu: subModulo.num_ordem_menu,
            des_modulo_secao: subModulo.des_modulo_secao.toUpperCase(),
            qtd_telas: Number(subModulo.qtd_telas),
          };
        });

        addUpdate('subModulos', subModulos, masterDetail, setMasterDetail);
        setLoadingTable(false);
        return setRows(subModulos);
      }
      setRows([]);
      setLoadingTable(false);
    },
    [masterDetail, reset],
  );

  const onSave = handleSubmit(async (dataFields) => {
    try {
      const dataForm = {
        ...dataFields,
        num_ordem_menu: 0,
        flg_requer_superadmin: flgSuperAdm,
        flg_inativo: flgInativo,
        masterDetail,
      };

      if (isUpdate) {
        const res = await api.put(`/modulo/${dataFields.cod_modulo}`, dataForm);
        const { success, message } = res.data;

        if (!success) {
          return toast.warning(message);
        }

        if (success) {
          resetFormData();
          setShowSearch(true);
          return toast.success(message);
        }
      }
      const res = await api.post('/modulo', dataForm);

      const { success, message } = res.data;
      if (success) {
        toast.success(message);
        return resetFormData();
      }
      return toast.warning(message);
    } catch (e: any) {
      if (e.status === 500) {
        return toast.error(e.data.message);
      }
    }
  });

  const handleDelete = async () => {
    const { cod_modulo } = getValues();
    const res = await api.delete(`/modulo/${cod_modulo}`);
    const { success, message } = res.data;
    if (!success) return toast.warn(message);
    resetFormData();
    setShowSearch(true);
    setUpdate(false);
    toast.success(message);
  };

  const onNew = () => {
    resetFormData();
    setUpdate(false);
    setShowSearch(false);
  };

  const handleOpenModal = useCallback((val: boolean) => {
    setOpenModal(val);
  }, []);

  const handleOpenOrderModal = useCallback((val: boolean) => {
    setOpenOrderModal(val);
  }, []);

  return (
    <ModuloContext.Provider
      value={{
        isUpdate,
        setUpdate,
        showSearch,
        setShowSearch,
        loadingTable,
        setLoadingTable,
        resetFormData,
        onRowClick,
        onNew,
        onSave,
        handleDelete,
        setRows,
        rows,
        openModal,
        handleOpenModal,
        formGeral,
        formModal,
        flgSuperAdm,
        setFlgSuperAdm,
        flgInativo,
        setFlgInativo,
        isEditing,
        setIsEditing,
        openOrderModal,
        handleOpenOrderModal,

        masterDetail,
        setMasterDetail,
      }}
    >
      {children}
    </ModuloContext.Provider>
  );
};
